import { Link } from "react-router";

import { AssociationFileBrowser } from "@/shared/components/AssociationFileBrowser";
import { useDevice } from "@/shared/domain/devices/hooks/useDevice";
import { useDeviceType } from "@/shared/domain/deviceTypes/hooks";
import { Callout, Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import { useDeviceContext } from "../Device/useDeviceContext";

import styles from "./DeviceConfigurations.module.css";

/** The URL search parameter that holds the folder open in the inherited section. */
export const INHERITED_FOLDER_PARAM = "inheritedFolder";

/**
 * Configurations tab of the device detail view: the device's own files, then,
 * when the device has a type, the files it inherits from that type. The two
 * sections page and open folders independently. The device section's open
 * folder is the rest of the URL path; the inherited section's is the
 * `inheritedFolder` search parameter.
 */
export function DeviceConfigurations() {
  const { deviceId, org } = useDeviceContext();
  const deviceQuery = useDevice(deviceId, org?.org_id);
  const device = deviceQuery.data;
  const deviceTypeQuery = useDeviceType(device?.device_type_id, device?.org_id);

  if (deviceQuery.error) {
    return <Callout variant="error">{deviceQuery.error.message}</Callout>;
  }
  if (!device) {
    return (
      <p className={styles.loading}>
        <Spinner aria-hidden />
        Loading device&hellip;
      </p>
    );
  }
  if (!device.universal_device_id) {
    return (
      <Callout variant="error">
        This device has no Roboto device ID, so its configurations cannot be
        listed.
      </Callout>
    );
  }

  const rootUrl = Goto.createDeviceConfigurationsRoute(deviceId);
  const deviceTypeId = device.device_type_id;
  // Undefined until the type loads; the labels below show the type's ID until then.
  const deviceTypeName = deviceTypeQuery.data?.name;

  return (
    <div className={styles.sections}>
      <section aria-labelledby="device-own-files" className={styles.section}>
        <h2 id="device-own-files" className={styles.heading}>
          This device
        </h2>
        <AssociationFileBrowser
          associationId={device.universal_device_id}
          orgId={device.org_id}
          rootUrl={rootUrl}
          rootLabel={deviceId}
          parentCrumbs={[]}
        />
      </section>
      {deviceTypeId && (
        <section
          aria-labelledby="device-inherited-files"
          className={styles.section}
        >
          <h2 id="device-inherited-files" className={styles.heading}>
            Inherited from type{" "}
            <Link to={Goto.createDeviceTypeRoute(deviceTypeId)}>
              {deviceTypeName ?? deviceTypeId}
            </Link>
          </h2>
          <p className={styles.note}>
            Every device of this type uses these configurations, unless it has
            its own file at the same path. Change them on the type&apos;s page.
            The search and sort above apply to this list too.
          </p>
          <AssociationFileBrowser
            associationId={deviceTypeId}
            orgId={device.org_id}
            rootUrl={rootUrl}
            folderSearchParam={INHERITED_FOLDER_PARAM}
            rootLabel={deviceTypeName ?? deviceTypeId}
            parentCrumbs={[]}
            inheritedBy={device.universal_device_id}
          />
        </section>
      )}
    </div>
  );
}
